// NATAMI — Punch list: PURE status/progress logic shared by the server routes,
// the PDF builder, the React module and the smoke tests. No prisma, no DOM.

import { PUNCH_AREAS, type PunchArea } from './punchListTemplate'

export const PUNCH_STATUSES = ['OPEN', 'IN_PROGRESS', 'DONE', 'VERIFIED'] as const
export type PunchStatus = (typeof PUNCH_STATUSES)[number]

export const PUNCH_STATUS_LABELS: Record<PunchStatus, { es: string; en: string }> = {
  OPEN: { es: 'Abierto', en: 'Open' },
  IN_PROGRESS: { es: 'En proceso', en: 'In progress' },
  DONE: { es: 'Terminado', en: 'Done' },
  VERIFIED: { es: 'Verificado', en: 'Verified' },
}

export function isPunchStatus(value: unknown): value is PunchStatus {
  return typeof value === 'string' && (PUNCH_STATUSES as readonly string[]).includes(value)
}

/** Quick "tap to advance": OPEN → IN_PROGRESS → DONE → VERIFIED, and VERIFIED
 *  stays VERIFIED (a tap must never silently re-open verified work — re-opening
 *  is an explicit choice in the status select). */
export function nextPunchStatus(current: PunchStatus): PunchStatus {
  const i = PUNCH_STATUSES.indexOf(current)
  return i < 0 || i >= PUNCH_STATUSES.length - 1 ? 'VERIFIED' : PUNCH_STATUSES[i + 1]
}

/**
 * The milestone stamps a status change implies.
 *   → DONE      completedAt = now (kept if already set), verifiedAt cleared
 *   → VERIFIED  verifiedAt = now (kept if already set), completedAt = existing ?? now
 *   → OPEN / IN_PROGRESS  both cleared (the work is not finished anymore)
 * Returns only the fields to write. Same status → {} (no churn).
 */
export function punchStatusStamps(
  current: { status: string; completedAt: Date | null; verifiedAt: Date | null },
  to: PunchStatus,
  now: Date,
): { completedAt?: Date | null; verifiedAt?: Date | null } {
  if (current.status === to) return {}
  if (to === 'DONE') return { completedAt: current.completedAt ?? now, verifiedAt: null }
  if (to === 'VERIFIED') return { completedAt: current.completedAt ?? now, verifiedAt: current.verifiedAt ?? now }
  return { completedAt: null, verifiedAt: null }
}

export function isPunchClosed(status: string): boolean {
  return status === 'DONE' || status === 'VERIFIED'
}

/** % of items DONE or VERIFIED, rounded down (never shows 100% while one is open). */
export function punchProgress(items: Array<{ status: string }>): { total: number; closed: number; percent: number } {
  const total = items.length
  const closed = items.filter((i) => isPunchClosed(i.status)).length
  return { total, closed, percent: total === 0 ? 0 : Math.floor((closed / total) * 100) }
}

/** Items grouped by area in the canonical PUNCH_AREAS order; empty areas are omitted.
 *  An unknown area string (legacy/bad row) lands in OTHER rather than vanishing. */
export function groupPunchItemsByArea<T extends { area: string }>(items: T[]): Array<{ area: PunchArea; items: T[] }> {
  const buckets = new Map<PunchArea, T[]>()
  for (const item of items) {
    const area = (PUNCH_AREAS as readonly string[]).includes(item.area) ? (item.area as PunchArea) : 'OTHER'
    const list = buckets.get(area) ?? []
    list.push(item)
    buckets.set(area, list)
  }
  return PUNCH_AREAS.filter((a) => buckets.has(a)).map((area) => ({ area, items: buckets.get(area)! }))
}
